import { useEffect, useState } from 'react'
import { FieldValues } from 'react-hook-form'

import {
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { DataService } from 'api/DataService'

import IconContainer from 'components/IconContainer/IconContainer'

import { currency } from 'utils'

import { theme } from 'styles/theme'

import CyclesTable from '../CyclesAccordion/CyclesTable'
import ItemRow from '../Infos/Components/ItemRow'
import { Record } from '../Infos/Components/ItemRow/ItemRow.interface'
import { CompositionProps } from './Composition.interface'

const Composition = <T extends FieldValues>({
  control,
  watch,
  appendItem,
  itemsFields,
  removeItem,
  totalValue,
  setValue,
  errors,
  updateDescriptionWithItems,
  isEdit,
  isPaymentLink = false,
  title = 'O que será cobrado',
  hasInitialProduct,
  isRecurrence = false,
  currentCycle,
  previewCycles = []
}: CompositionProps<T>) => {
  const [isCyclesModalOpen, setIsCyclesModalOpen] = useState(false)

  const hasCyclesPreview = previewCycles.length > 0

  /**
   * Desabilitado tem mais de um motivo, e dizer qual poupa o merchant de
   * procurar: cobrança única não tem ciclos; recorrência sem item ainda não tem
   * o que calcular.
   */
  const cyclesPreviewTooltip = hasCyclesPreview
    ? 'Ver prévia dos ciclos'
    : !isRecurrence
    ? 'A prévia dos ciclos está disponível apenas em cobranças recorrentes.'
    : 'Escolha um plano para ver a prévia dos ciclos.'
  const [products, setProducts] = useState<Record[]>([])
  const [hasFetched, setHasFetched] = useState<boolean>(false)

  const getAutocompleteProducts = async () => {
    setHasFetched(false)
    const responseProductsAutocomplete = await DataService({
      type: 'GET',
      url: '/v1/products_autocomplete'
    })
    if (responseProductsAutocomplete.status === 200) {
      const productsList = responseProductsAutocomplete.data.records.map(
        (record: any) => ({
          label: record.name,
          value: record.uuid,
          product: record
        })
      )

      setProducts([{ label: '', value: 'custom' }, ...productsList])
    }
    setHasFetched(true)
  }

  useEffect(() => {
    getAutocompleteProducts()
  }, [])

  return (
    <Stack gap="16px" my={2} mt={3}>
      <Stack gap={0.5} mb={-1}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="mdBold">{title}</Typography>

          {/* Sempre visível, para o merchant saber que a prévia existe. Sem
              ciclos calculados ele fica desabilitado e o tooltip diz por quê. */}
          <Tooltip title={cyclesPreviewTooltip}>
            {/* O span é necessário: o MUI não dispara tooltip sobre botão
                desabilitado, que não emite eventos de mouse. */}
            <span>
              <IconButton
                size="small"
                data-cy="open-cycles-preview"
                disabled={!hasCyclesPreview}
                onClick={() => setIsCyclesModalOpen(true)}
                sx={{ padding: '2px' }}
              >
                <Icon
                  icon="event"
                  sx={{ fontSize: '18px' }}
                  colorSX={
                    hasCyclesPreview
                      ? theme.palette.neutral[400]
                      : theme.palette.neutral[600]
                  }
                />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Modal
        open={isCyclesModalOpen}
        handleClose={() => setIsCyclesModalOpen(false)}
        maxWidth="700px"
        width="100%"
        maxHeight="90vh"
      >
        <ModalBody>
          <ModalHeader onClose={() => setIsCyclesModalOpen(false)}>
            <Stack direction="row" alignItems="center" gap={2}>
              <IconContainer icon="event" />
              <Typography variant="mdBold">Prévia dos ciclos</Typography>
            </Stack>
          </ModalHeader>
          <ModalContent>
            <Box mt={2}>
              <CyclesTable cycles={previewCycles} />
            </Box>
          </ModalContent>
          <ModalFooter>
            <Button
              variantButton="outlined"
              onClick={() => setIsCyclesModalOpen(false)}
            >
              Fechar
            </Button>
          </ModalFooter>
        </ModalBody>
      </Modal>

      {!!itemsFields &&
        itemsFields.map((field, index) => (
          <ItemRow
            hasInitialProduct={hasInitialProduct}
            // field.id do useFieldArray, não field.uuid: item recém-adicionado nasce com
            // uuid '' e dois deles gerariam a mesma key, fazendo o React embaralhar o
            // estado entre as linhas.
            key={field.id}
            field={field}
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            products={products}
            hasFetched={hasFetched}
            itemsFields={itemsFields}
            removeItem={removeItem}
            updateDescriptionWithItems={updateDescriptionWithItems}
            refetchProducts={getAutocompleteProducts}
            isEdit={isEdit}
            isPaymentLink={isPaymentLink}
            isRecurrence={isRecurrence}
            currentCycle={currentCycle}
          />
        ))}

      <Box sx={{ height: '1px', bgcolor: theme.palette.neutral[700] }}></Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button
          onClick={() =>
            !!appendItem &&
            appendItem({
              description: '',
              price: 0,
              total: 0,
              typedValue: '',
              quantity: 1,
              uuid: '',
              tags: [],
              // Item novo numa recorrência já existente começa no próximo ciclo
              // (current_cycle + 1), para não entrar em ciclos já decorridos.
              // O backend impõe esse piso também.
              start_cycle: isRecurrence ? Number(currentCycle || 0) + 1 : 1,
              total_cycles: 0,
              ...(isPaymentLink && {
                enable_customer_select_quantity: false,
                minimum_quantity: 1
              })
            })
          }
          variantButton="ghost"
          sx={{ width: 'fit-content', mt: '-10px' }}
        >
          <Stack direction="row" gap="5px" alignItems="center">
            <Icon
              icon="add"
              sx={{ color: theme.palette.neutral[400], fontSize: '18px' }}
            />
            <Typography
              sx={{
                color: theme.palette.neutral[400],
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Adicionar item
            </Typography>
          </Stack>
        </Button>

        <Stack direction="row" gap="50px" mt={'-10px'}>
          <Typography variant="smBold" color={theme.palette.neutral[400]}>
            Total:
          </Typography>
          <Typography variant="smBold" color={theme.palette.neutral[100]}>
            R$ {currency(totalValue || 0)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default Composition
